/**
 * EAA-1825: the terminating condition for the portal's sign-in loop. (Observed
 * during EAA-1846's QA sweep against the running stack; fixed here because the
 * portal's full-screen auth cards and their failure states are this ticket's.)
 *
 * THE BUG THIS EXISTS FOR. Every 401 site in the portal (LauncherPage's
 * `/me/access`, ConsoleLayout's `/me/admin`, CommandPalette's `/search`) reacted
 * to a typed unauthorized error by calling `client.login()` — "the session
 * portal-api sees is no longer valid, so re-authenticate". That is correct only
 * when the 401 means the OIDC session ended. When portal-api rejects a bearer
 * the IdP is perfectly happy to keep minting (a wrong audience, a missing
 * client scope, a clock skew, an API deployed without the realm's public key),
 * the reaction is a loop that never settles:
 *
 *     GET /me/access → 401 → login() → Keycloak (SSO cookie still valid, no
 *     prompt) → /callback → code exchange → 200 token → back to `/` → 401 → …
 *
 * Each leg is a FULL PAGE LOAD, which is why it shows up as the app reloading
 * itself forever, with the network log repeating config.json →
 * openid-configuration → token → a burst of 401s.
 *
 * WHY A "BOUNCE FLAG" CANNOT FIX IT. EAA-1762 hit the same shape in
 * web-content and first tried a one-shot sessionStorage flag. A flag marks a
 * LEG, and it is consumed on the return leg — so a failure that repeats simply
 * re-arms it and the ping-pong continues. Terminating the loop needs an attempt
 * COUNT that survives across page loads, which is what this module is.
 *
 * WHY THE COUNTER IS NOT CLEARED ON "authenticated". In this loop the OIDC
 * sign-in SUCCEEDS on every leg — a fresh access token is minted each time. So
 * "we have a session now" is not evidence the loop is over, and clearing on it
 * would make the counter unable to ever reach its limit. The only automatic
 * reset is the time window: redirects that are more than `WINDOW_MS` apart are
 * unrelated history (a normal expiry-and-renew hours later), not a loop.
 */

const ATTEMPTS_KEY = 'eaa1825:signin-attempts';

/**
 * Redirects to the IdP allowed inside the window before we call it a loop. Two
 * covers the legitimate worst case — the initial sign-in, plus one genuine
 * re-authentication after it — so the THIRD redirect is the first one that is
 * provably going in circles, and it never happens. Same budget web-content's
 * EAA-1762 guard settled on, for the same reason: any higher and the user sits
 * through more full page reloads before seeing the failure.
 */
const MAX_ATTEMPTS = 2;

const WINDOW_MS = 60_000;

/** Set for the lifetime of ONE page load, so several components reacting to the
 *  same 401 burst (the launcher, the shell and the palette all fire at once)
 *  spend one attempt between them rather than the whole budget in one leg. */
let redirectInFlight = false;

export type SignInRequestResult =
    /** A redirect to the IdP was started; the page is going away. */
    | 'redirecting'
    /** Another component already started one this page load — nothing to do. */
    | 'in-flight'
    /** Budget spent: the caller must render a failure instead of redirecting. */
    | 'looping';

interface SignInAttempts {
    count: number;
    firstAt: number;
}

function readAttempts(): SignInAttempts {
    const empty: SignInAttempts = { count: 0, firstAt: 0 };
    let raw: string | null = null;
    try {
        raw = window.sessionStorage.getItem(ATTEMPTS_KEY);
    } catch {
        return empty; // sessionStorage unavailable (private mode) — never block on it
    }
    if (!raw) return empty;
    try {
        const parsed = JSON.parse(raw) as Partial<SignInAttempts>;
        if (typeof parsed.count !== 'number' || typeof parsed.firstAt !== 'number') return empty;
        if (Date.now() - parsed.firstAt > WINDOW_MS) return empty;
        return { count: parsed.count, firstAt: parsed.firstAt };
    } catch {
        return empty;
    }
}

function recordAttempt(): void {
    const prev = readAttempts();
    const next: SignInAttempts = { count: prev.count + 1, firstAt: prev.firstAt || Date.now() };
    try {
        window.sessionStorage.setItem(ATTEMPTS_KEY, JSON.stringify(next));
    } catch {
        /* sessionStorage unavailable — the loop guard degrades to the
           per-page-load `redirectInFlight` check only. */
    }
}

/** True when the budget is already spent — a redirect now would only reload. */
export function isSignInLooping(): boolean {
    return readAttempts().count >= MAX_ATTEMPTS;
}

/** Called when the user explicitly retries, and on sign-out: whatever loop was
 *  running is over by decision, so the next redirect starts from a full budget. */
export function clearSignInAttempts(): void {
    redirectInFlight = false;
    try {
        window.sessionStorage.removeItem(ATTEMPTS_KEY);
    } catch {
        /* nothing to clear if the store is unavailable */
    }
}

/**
 * The ONLY way the portal should redirect to the IdP. Counts the attempt, and
 * refuses once the count proves the round trip is not converging — the caller
 * then renders the failure (see `AuthProvider`'s `session-error` status) instead
 * of reloading the tab again.
 */
export async function requestSignIn(
    login: (returnTo?: string) => Promise<void>,
    returnTo?: string
): Promise<SignInRequestResult> {
    if (redirectInFlight) return 'in-flight';
    if (isSignInLooping()) return 'looping';

    redirectInFlight = true;
    recordAttempt();
    try {
        await login(returnTo);
    } catch {
        // The redirect itself failed (metadata fetch down, popup blocked). Let a
        // later attempt try again rather than pinning the page on a stale flag.
        redirectInFlight = false;
    }
    return 'redirecting';
}
